import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import '../legal/legal.css';
import { CookieMark } from '../legal/illustrations';
import {
  COOKIE_LANGUAGE_OPTIONS,
  ConsentCategories,
  CookieLanguage,
  OPEN_SETTINGS_EVENT,
  acceptAll,
  categoriesForCurrentContext,
  deniedForCurrentContext,
  getCategoryMeta,
  getCookieCopy,
  getCookieLanguage,
  readConsent,
  rejectAll,
  setCookieUiLanguage,
  writeConsent,
} from '../legal/cookieConsent';

// Custom dropdown (not a native <select>) so the open menu can always show
// full language names while the closed button shrinks to flag-only on small
// screens. Rendered via a portal so it isn't clipped by the dialog card's
// `overflow: hidden`.
function CookieLanguageSwitcher({
  lang,
  narrow,
  onChange,
}: {
  lang: CookieLanguage;
  narrow: boolean;
  onChange: (next: CookieLanguage) => void;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ bottom: number; right: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  const current =
    COOKIE_LANGUAGE_OPTIONS.find((o) => o.code === lang) ?? COOKIE_LANGUAGE_OPTIONS[0];

  const openMenu = () => {
    const rect = btnRef.current?.getBoundingClientRect();
    if (rect) {
      setPos({
        bottom: window.innerHeight - rect.top + 6,
        right: window.innerWidth - rect.right,
      });
    }
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (btnRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onResize = () => setOpen(false);
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  return (
    <div className="se-cc-lang">
      <button
        type="button"
        ref={btnRef}
        className="se-cc-lang-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Language"
        onClick={() => (open ? setOpen(false) : openMenu())}
      >
        <span className="se-cc-lang-flag" aria-hidden="true">
          {current.flag}
        </span>
        {!narrow && <span className="se-cc-lang-label">{current.label}</span>}
      </button>
      {open &&
        pos &&
        createPortal(
          <ul
            ref={menuRef}
            className="se-cc-lang-menu"
            role="listbox"
            aria-label="Language"
            style={{ position: 'fixed', bottom: pos.bottom, right: pos.right }}
          >
            {COOKIE_LANGUAGE_OPTIONS.map((opt) => (
              <li key={opt.code}>
                <button
                  type="button"
                  role="option"
                  aria-selected={opt.code === lang}
                  className={`se-cc-lang-opt${opt.code === lang ? ' se-active' : ''}`}
                  onClick={() => {
                    onChange(opt.code);
                    setOpen(false);
                  }}
                >
                  <span aria-hidden="true">{opt.flag}</span>
                  <span className="se-cc-lang-opt-label">{opt.label}</span>
                </button>
              </li>
            ))}
          </ul>,
          document.body,
        )}
    </div>
  );
}

/**
 * Cookie-consent dialog. Mount once near the app root. It appears on first
 * visit (no stored consent) and can be re-opened anywhere via
 * `openCookieSettings()` which dispatches OPEN_SETTINGS_EVENT.
 */
export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [choices, setChoices] = useState<ConsentCategories>(() =>
    deniedForCurrentContext(),
  );
  const [lang, setLang] = useState<CookieLanguage>(() => getCookieLanguage());
  const [narrowLang, setNarrowLang] = useState(false);

  const copy = getCookieCopy();
  const categoryMeta = getCategoryMeta();

  const handleLanguageChange = useCallback((next: CookieLanguage) => {
    setCookieUiLanguage(next);
    setLang(next);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 480px)');
    const update = () => setNarrowLang(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    const stored = readConsent();
    if (stored) {
      setChoices(categoriesForCurrentContext(stored.categories));
    }

    let forceSettings = false;
    try {
      forceSettings = new URLSearchParams(window.location.search).get('cookieSettings') === '1';
    } catch {
      /* ignore */
    }

    if (forceSettings) {
      if (stored) setChoices(categoriesForCurrentContext(stored.categories));
      setShowSettings(true);
      setVisible(true);
    } else if (!stored) {
      setVisible(true);
    }
  }, []);

  useEffect(() => {
    const onOpen = () => {
      const stored = readConsent();
      setChoices(stored ? categoriesForCurrentContext(stored.categories) : deniedForCurrentContext());
      setShowSettings(true);
      setVisible(true);
    };

    window.addEventListener(OPEN_SETTINGS_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, onOpen);
  }, []);

  const close = useCallback(() => {
    setVisible(false);
    setShowSettings(false);
  }, []);

  const handleAcceptAll = useCallback(() => {
    const state = acceptAll();
    setChoices(state.categories);
    close();
  }, [close]);

  const handleRejectAll = useCallback(() => {
    const state = rejectAll();
    setChoices(state.categories);
    close();
  }, [close]);

  const handleSaveSelection = useCallback(() => {
    const state = writeConsent(choices);
    setChoices(state.categories);
    close();
  }, [choices, close]);

  const toggle = (key: keyof ConsentCategories) =>
    setChoices((prev) =>
      categoriesForCurrentContext({ ...prev, [key]: !prev[key] }),
    );

  if (!visible) return null;

  return (
    <div
      className="se-cc-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="se-cc-title"
    >
      <div className="se-cc-card">
        <div className="se-cc-head">
          <CookieMark />
          <h2 id="se-cc-title">{copy.title}</h2>
        </div>

        <div className="se-cc-body">
          {copy.intro.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}

          <p>
            {copy.linksPrefix}{' '}
            <a href="/datenschutz" target="_blank" rel="noopener noreferrer">
              {copy.privacy}
            </a>{' '}
            und der{' '}
            <a href="/cookies" target="_blank" rel="noopener noreferrer">
              {copy.policy}
            </a>
            .
          </p>

          {showSettings &&
            categoryMeta.map((cat) => (
              <div className="se-cc-cat" key={cat.key}>
                <div>
                  <p className="se-cc-cat-title">{cat.title}</p>
                  <p className="se-cc-cat-desc">{cat.desc}</p>
                </div>
                <label className="se-switch">
                  <input
                    type="checkbox"
                    checked={choices[cat.key]}
                    disabled={cat.locked}
                    onChange={() => toggle(cat.key)}
                    aria-label={cat.title}
                  />
                  <span className="se-slider" />
                </label>
              </div>
            ))}
        </div>

        <div className="se-cc-actions">
          {showSettings ? (
            <>
              <button
                type="button"
                className="se-btn se-btn-ghost"
                onClick={handleSaveSelection}
              >
                {copy.saveSelection}
              </button>
              <button
                type="button"
                className="se-btn se-btn-primary"
                onClick={handleAcceptAll}
              >
                {copy.acceptAll}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="se-btn se-btn-ghost"
                onClick={handleRejectAll}
              >
                {copy.rejectAll}
              </button>
              <button
                type="button"
                className="se-btn se-btn-primary"
                onClick={handleAcceptAll}
              >
                {copy.acceptAll}
              </button>
              <button
                type="button"
                className="se-btn se-btn-text"
                onClick={() => setShowSettings(true)}
              >
                {copy.settings}
              </button>
            </>
          )}
        </div>

        <div className="se-cc-foot">
          <div className="se-cc-foot-links">
            <a href="/datenschutz" target="_blank" rel="noopener noreferrer">
              {copy.footerPrivacy}
            </a>
            <a href="/impressum" target="_blank" rel="noopener noreferrer">
              {copy.footerImprint}
            </a>
            <a href="/cookies" target="_blank" rel="noopener noreferrer">
              {copy.footerPolicy}
            </a>
          </div>
          <CookieLanguageSwitcher lang={lang} narrow={narrowLang} onChange={handleLanguageChange} />
        </div>
      </div>
    </div>
  );
}